import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SearchBar from '../../../../Components/Utility/FilterAction/SearchBar';
import { BombiixApprovalBar } from '../../../../Components/BombiixApprovalBar';
import { checkArray } from '../../../../Components/helper';
import { fetchCareLabelLayout, approveRejectLayout, deleteLayoutPdf, uploadPDFforCareLayout, deleteCareComments } from '../store/CARELABEL.action';
import UploadCareLayoutPDF from './UploadCareLayoutPDF';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';

/* Values are strings, not the numbers used in the /master-design demo: OrdersReports is
   written through the native driver (db.collection), so status is stored exactly as sent
   and the row badges below compare it against '1' / '0'. */
const CARE_APPROVAL_OPTIONS = [
  { label: 'Approved', value: '1' },
  { label: 'Rejected', value: '0', variant: 'red' },
];

/* Care layouts allowed 200 characters where the rest of the app allowed 80, so the limit is
   passed explicitly — BombiixApprovalBar defaults to 80 and would otherwise shorten it. */
const CARE_COMMENT_MAX_LENGTH = 200;

function CareLayout({ devOrderId, CareApproveRejectAccess, CareAddEditAccess, CareDeleteAccess, isProductArchived, isSupplierLoggedIn }) {
  const { t } = useTranslation('common');

  const reducerState = useSelector((redState) => redState);
  const {
    CareLabelProductReducer: { layoutCareData },
    BOMConfigDetails: { productDetails },
  } = reducerState;
  const dispatch = useDispatch();
  const [state, setState] = useState({
    layoutData: [],
    selectedId: [],
    searchQuery: '',
    deleteModal: false,
    deletedId: '',
    submitDisable: false,
  });

  const [previewPDf, setPreviewPDf] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [approvalComment, setApprovalComment] = useState('');
  const [approvalDate, setApprovalDate] = useState(new Date());
  const [showUpload, setShowUpload] = useState(false);

  const handleCheckAll = async (e) => {
    if (e.target.checked) {
      const allIds = state.layoutData && checkArray(state.layoutData) && state.layoutData.map((ele) => ele._id);
      if (allIds) {
        setState((prevState) => ({
          ...prevState,
          selectedId: allIds,
        }));
      }
    } else {
      setState((prevState) => ({
        ...prevState,
        selectedId: [],
      }));
    }
  };

  const isallChecked = !!(checkArray(state.layoutData) && state?.selectedId.length === state.layoutData.length);

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      layoutData: layoutCareData,
    }));
  }, [layoutCareData]);

  const showPreview = (pdf) => {
    setPreviewPDf(pdf);
  };

  /**
   * Toggles selection for a single care layout row. Selection drives the approval bar only and
   * deliberately leaves the preview pane alone, so reviewers can tick rows for approval without
   * the right-hand PDF switching under them. Preview is opened from the file name instead.
   * @param {Boolean} isLibChecked Next checked state of the row checkbox.
   * @param {String} libId Care layout document id being toggled.
   * @returns {Promise<void>}
   */
  const handleCheckLibrary = async (isLibChecked, libId) => {
    const existingIds = state?.selectedId;
    if (isLibChecked && !existingIds.includes(libId)) {
      setState((prevState) => ({
        ...prevState,
        selectedId: [...existingIds, libId],
      }));
    } else {
      const removedArray = existingIds?.filter((eleId) => eleId !== libId);
      if (checkArray(removedArray)) {
        setState((prevState) => ({
          ...prevState,
          selectedId: removedArray,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          selectedId: [],
        }));
      }
    }
  };

  const fetchCareLayoutData = async () => {
    await dispatch(fetchCareLabelLayout(devOrderId));
  };

  /**
   * A comment is compulsory for both approving and rejecting. The bar performs no validation
   * of its own, so the invalid state is derived here and only shown once a decision is picked,
   * keeping the field neutral until the reviewer engages with the bar.
   */
  const isApprovalCommentInvalid = !!approvalStatus && approvalComment.trim().length === 0;

  /**
   * Submits the approval bar decision for every selected care layout document. Guards on a
   * chosen status because the API treats any status other than 1/'1' as a rejection, so an
   * unset value would silently reject the selection, and on the compulsory comment so a
   * blank review cannot be saved if the button is ever enabled by other means.
   * @returns {Promise<void>}
   */
  const handleApprovalSave = async () => {
    if (!approvalStatus || isApprovalCommentInvalid || !checkArray(state?.selectedId)) {
      return;
    }
    const jsonString = JSON.stringify(state?.selectedId);
    const formattedString = jsonString.replace(/"/g, '"');
    const commentParams = {
      comment: approvalComment,
      commentDate: approvalDate || new Date(),
      reportIds: formattedString,
      status: approvalStatus,
    };
    const res = await dispatch(approveRejectLayout(devOrderId, commentParams, t));
    if (res) {
      setState((prev) => ({
        ...prev,
        selectedId: [],
      }));
      fetchCareLayoutData();
    }
  };

  useEffect(() => {
    if (!checkArray(state?.selectedId)) {
      setApprovalStatus(null);
      setApprovalComment('');
      setApprovalDate(new Date());
    }
  }, [state?.selectedId]);

  const handleCloseDeleteModal = () => {
    setState((prevState) => ({
      ...prevState,
      deletedId: null,
      deleteModal: false,
    }));
  };
  /**
   * Deletes the selected care layout PDF and clears the preview pane when the removed
   * document is the one currently rendered, preventing a deleted file staying on screen.
   * @returns {Promise<void>}
   */
  const confirmDelete = async () => {
    const deletedLayout = checkArray(state?.layoutData) ? state.layoutData.find((item) => item._id === state?.deletedId) : null;
    const res = await dispatch(deleteLayoutPdf(devOrderId, state?.deletedId, t));
    if (res) {
      if (deletedLayout?.pdf) {
        setPreviewPDf((currentPreview) => (currentPreview === deletedLayout.pdf ? null : currentPreview));
      }
      handleCloseDeleteModal();
      fetchCareLayoutData();
    }
  };

  const handleDeletePdf = (id) => {
    setState((prevState) => ({
      ...prevState,
      deletedId: id,
      deleteModal: true,
    }));
  };

  const handleSearchInputChange = (event) => {
    const query = event.target.value;
    setState((prev) => ({
      ...prev,
      searchQuery: query,
    }));
  };

  useEffect(() => {
    if (state?.searchQuery) {
      const filterData = layoutCareData?.filter((item) => item.pdfTitle.toLowerCase().includes(state?.searchQuery.toLowerCase()));
      setState((prev) => ({
        ...prev,
        layoutData: filterData,
      }));
    } else {
      setState((prev) => ({
        ...prev,
        layoutData: layoutCareData,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.searchQuery]);

  const openPDFup = () => {
    setShowUpload(true);
  };

  const clearState = () => {
    setShowUpload(false);
  };

  /**
   * Uploads the selected care layout PDF (a freshly picked file or an existing asset).
   * The Save button is disabled only for the duration of the request and is always
   * re-enabled afterwards, so a failed upload or a closed modal never leaves it stuck.
   * @param {Object|string} data Selected document payload, or the string 'close'.
   */
  const submitData = async (data) => {
    if (data === 'close') {
      clearState();
      return;
    }
    setState((prevState) => ({
      ...prevState,
      submitDisable: true,
    }));
    const formData = new FormData();
    if (data.existingAsset) {
      formData.append("existingAssetId", data.existingAsset.id);
    } else {
      formData.append("pdf", data.document);
      formData.append("isAddToAssetLibrary", data.addToAssetLibrary === true);
    }
    formData.append("pdfTitle", data.fileName);
    formData.append("loggedInUserId", "5aab62e11bac316c04d93ff9");
    formData.append("reportId", null);
    formData.append("libId", false);
    formData.append("isRenew", false);
    formData.append("fileExpiry", null);
    try {
      const res = await dispatch(uploadPDFforCareLayout(devOrderId, formData, t));
      if (res) {
        clearState();
        fetchCareLayoutData();
      }
    } finally {
      setState((prevState) => ({
        ...prevState,
        submitDisable: false,
      }));
    }
  };

  const handleDeleteComments = async (data) => {
    const newData = [];
    newData.push(data);
    const jsonString = JSON.stringify(newData);
    const formattedString = jsonString.replace(/"/g, '"');
    const params = {
      reportIds: formattedString,
    };
    const res = await dispatch(deleteCareComments(devOrderId, params, t));
    if (res) {
      fetchCareLayoutData();
    }
  };
  const getStatus = (data) => {
    return data.status === '1' ? 'approved' : 'pending';
  };
  return (
    <div className="full-width-block accordion-block care-layout-container layout-container">
      <div className="full-width-block">
        <div className="row">
          <div className="col-md-4 col-lg-4 col-sm-12">
            <div className="filter-container care-layout-search">
              <SearchBar
                onSearch={(e) => {
                  handleSearchInputChange(e);
                }}
                value={state.searchQuery}
                placeholder={t('TAKING_DATES.SEARCH_CARE_LABEL')}
                isProductArchived={productDetails?.isArchived}
              />
              {(CareAddEditAccess || isSupplierLoggedIn) && (
                <CustomTooltip placement="top" tooltipText={t('subMenu.UPLOAD')}>
                  <button aria-label="Upload" className="btn btn-outline md mr-0" onClick={() => openPDFup()} type="button" disabled={isProductArchived}>
                    <i className="icon-Upload-1 f-24" />
                  </button>
                </CustomTooltip>
              )}
            </div>
            <div className="card mt-16 common-listing-card min-height">
              <div className="card-header">
                {CareApproveRejectAccess ? (
                  <div className="common-checkbox">
                    <input
                      type="checkbox"
                      name="check_all_layout"
                      id="check_all_layout"
                      onChange={(e) => handleCheckAll(e)}
                      checked={isallChecked}
                      disabled={isProductArchived}
                    />
                    <label htmlFor="check_all_layout" className="chklabel">
                      <span className="f-16 font-heavy">{t('TAKING_DATES.CARE_LABEL_LAYOUT')}</span>
                    </label>
                  </div>
                ) : (
                  <label className="chklabel">
                    <span className="f-18">{t('TAKING_DATES.CARE_LABEL_LAYOUT')}</span>
                  </label>
                )}
              </div>
              <div className="care-layout-body">
                {state?.layoutData
                  ? state?.layoutData?.map((data, i) => {
                      return (
                        <div key={data?._id} className="item-container">
                          <div className="d-flex align-items-center">
                            {CareApproveRejectAccess && (
                              <div className="common-checkbox">
                                <input
                                  type="checkbox"
                                  name={`layout_${i}`}
                                  id={`layout_${i}`}
                                  checked={!!state?.selectedId?.includes(data._id)}
                                  onChange={(e) => handleCheckLibrary(e.target.checked, data._id)}
                                  disabled={isProductArchived}
                                />
                                <label htmlFor={`layout_${i}`} className="chklabel" />
                              </div>
                            )}
                            <div className="file-section" key={data._id}>
                              <div
                                className="care-layout-name"
                                onClick={() => showPreview(data?.pdf)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    showPreview(data?.pdf);
                                  }
                                }}
                                tabIndex={0}
                                role="button"
                              >
                                <span title={data?.pdfTitle} className="text-ellipsis f-16 text-black">
                                  {data?.pdfTitle}
                                </span>
                                <span title={data?.pdfName} className="f-16 text-black text-ellipsis">
                                  &nbsp;|&nbsp;{data?.pdfName}
                                </span>
                              </div>
                              {CareDeleteAccess && !isSupplierLoggedIn && (
                                <button
                                  aria-label="delete"
                                  className="btn  btn-delete p-0"
                                  type="button"
                                  onClick={() => handleDeletePdf(data._id)}
                                  disabled={isProductArchived}
                                >
                                  <i className="icon-Delete f-24" />
                                </button>
                              )}
                            </div>
                          </div>

                          {data.status === '0' || data.status === '1' ? (
                            <div className="pl-4">
                              <span className="card.color-card .card-header">
                                <span className="">
                                  <span className="f-16 text-black font-heavy">{t('TAKING_DATES.STATES')}</span>:{' '}
                                  <span className="f-16 text-black font-medium">{data.status === '0' ? 'Rejected' : 'Approved'}&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                                </span>
                                <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip">{data?.comment}</Tooltip>}>
                                  <span className="text-black">{t('TAKING_DATES.SHOW_COMMENT')}</span>
                                </OverlayTrigger>
                                {CareDeleteAccess && !isSupplierLoggedIn && (
                                  <span
                                    className="icon-Delete ml-8 mt-1"
                                    aria-hidden="true"
                                    title={t('TAKING_DATES.REMOVE_COMMENT')}
                                    type="button"
                                    onClick={() => handleDeleteComments(data._id)}
                                  />
                                )}
                              </span>
                            </div>
                          ) : null}
                          <span className={`${data.status === '0' ? 'rejected' : getStatus(data)} status-bar`} />
                        </div>
                      );
                    })
                  : null}
              </div>
            </div>
          </div>
          <div className="col-md-8 col-lg-8 col-sm-12">
            <div className="doc-container overflow-hidden">
              {previewPDf ? (
                <iframe title={t('TAKING_DATES.FILE_PREVIEW')} id="viwer" className="care-layout-preview" src={previewPDf} frameBorder="0" />
              ) : (
                <div className="text-center" style={{ position: 'relative' }}>
                  <p className=" f-24 mb-0 font-heavy">{t('TAKING_DATES.PDF_PREVIEW')}</p>
                  <span className=" f-20 font-medium">{t('TAKING_DATES.TAB_CARE_LABEL')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {CareApproveRejectAccess && checkArray(state?.selectedId) && (
        <BombiixApprovalBar
          className="bombiix-approval-bar--fixed"
          options={CARE_APPROVAL_OPTIONS}
          value={approvalStatus}
          onChange={setApprovalStatus}
          showDatePicker
          date={approvalDate}
          onDateChange={setApprovalDate}
          showComment
          showCommentCounter
          commentMaxLength={CARE_COMMENT_MAX_LENGTH}
          comment={approvalComment}
          onCommentChange={setApprovalComment}
          commentInvalid={isApprovalCommentInvalid}
          onSave={handleApprovalSave}
          saveDisabled={!approvalStatus || isApprovalCommentInvalid}
          disabled={isProductArchived}
        />
      )}
      {showUpload ? (
        <UploadCareLayoutPDF showUpload={showUpload} devOrderId={devOrderId} submitData={submitData} submitDisable={state?.submitDisable} />
      ) : null}
      {state?.deleteModal && (
        <ConfirmationPopup
          onOpen={state.deleteModal}
          onConfirm={() => confirmDelete()}
          title={t('TAKING_DATES.DELETE_CARE_LABEL')}
          message={t('TAKING_DATES.DELETE_CARE_MESSAGE')}
          toggle={() => handleCloseDeleteModal()}
        />
      )}
    </div>
  );
}

export default CareLayout;
